import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { BRAND, waLink } from "../data/content";

const LINKS = [
  { to: "/about", label: "About Us" },
  { to: "/services", label: "Services" },
  { to: "/venues", label: "Venues" },
  { to: "/gallery", label: "Gallery" },
  { to: "/wedding-ideas", label: "Wedding Ideas" },
  { to: "/testimonials", label: "Testimonials" },
  { to: "/enquire", label: "Enquire", accent: true },
  { to: "/contact", label: "Contact Us" },
];

export const WhatsAppIcon = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const lightTop = pathname === "/enquire" || pathname === "/contact";
  const solid = scrolled || lightTop;

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          solid ? "bg-ivory/85 backdrop-blur-xl border-b border-gold/20 py-3" : "bg-transparent py-5"
        }`}
        data-testid="navbar"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 md:px-12">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
            <img
              src="/logo.jpg"
              alt="The Big Wedd emblem"
              data-testid="nav-logo-emblem"
              className="h-10 w-10 md:h-12 md:w-12 rounded-full object-cover ring-1 ring-gold/40"
            />
            <span className="leading-none">
              <span className="font-serif text-2xl md:text-[26px] tracking-[0.08em] text-ivory" style={solid ? { color: "#0C3B2E" } : {}}>
                THE BIG <span className="italic text-gold">WEDD</span>
              </span>
              <span
                className="mt-1 hidden md:block text-[9px] uppercase tracking-[0.35em]"
                style={{ color: solid ? "#A88544" : "rgba(253,251,247,0.75)" }}
              >
                {BRAND.tagline}
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-6">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`whitespace-nowrap text-[10.5px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                  solid ? "text-ink/80 hover:text-gold-text" : "text-ivory/85 hover:text-gold-light"
                } ${pathname === l.to ? "text-gold-text" : ""} ${l.accent ? "!text-gold-text" : ""}`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <Link
              to="/enquire"
              data-testid="nav-cta-plan-my-wedding"
              className="hidden md:inline-flex whitespace-nowrap rounded-full bg-gold px-6 py-3 text-[11px] font-medium uppercase tracking-[0.2em] text-ink transition-all duration-500 hover:bg-emerald hover:text-gold-light"
            >
              Plan My Wedding
            </Link>
            <button
              onClick={() => setOpen(!open)}
              data-testid="nav-mobile-toggle"
              aria-label="Menu"
              className={`lg:hidden flex flex-col gap-1.5 p-2 ${solid ? "text-emerald" : "text-ivory"}`}
            >
              <span className={`block h-px w-7 bg-current transition-transform duration-300 ${open ? "translate-y-[7px] rotate-45" : ""}`} />
              <span className={`block h-px w-7 bg-current transition-opacity duration-300 ${open ? "opacity-0" : ""}`} />
              <span className={`block h-px w-7 bg-current transition-transform duration-300 ${open ? "-translate-y-[7px] -rotate-45" : ""}`} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-emerald px-8 lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <nav className="flex flex-col gap-2">
              <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.02 }}>
                <Link to="/" className="font-serif text-4xl text-ivory hover:text-gold-light transition-colors" data-testid="nav-mobile-link-home">
                  Home
                </Link>
              </motion.div>
              {LINKS.map((l, i) => (
                <motion.div key={l.to} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.06 * (i + 1) }}>
                  <Link to={l.to} className={`font-serif text-4xl hover:text-gold-light transition-colors ${l.accent ? "text-gold-light" : "text-ivory"}`} data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <div className="mt-10 flex flex-col gap-4">
              <Link to="/enquire" data-testid="nav-mobile-cta" className="inline-flex w-max rounded-full bg-gold px-8 py-4 text-xs font-medium uppercase tracking-[0.2em] text-ink">
                Plan My Wedding
              </Link>
              <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="nav-mobile-whatsapp" className="inline-flex w-max items-center gap-2 text-sm text-ivory/80">
                <WhatsAppIcon className="h-4 w-4" /> Chat on WhatsApp
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
